import api, { queryClient } from "@app/config/api/api";
import type { ExperimentId } from "@globals";
import { notifications } from "@lib/notification/notification";
import { useQuery } from "@tanstack/react-query";

// Re-exported so callers keep importing the history translation from the queries module, while the
// pure (api-client-free) implementation lives on its own and stays unit-testable in the node project.
export { translateBackendMessages } from "./translateBackendMessages";

export const agentSessionMessagesOptions = (experiment_id: ExperimentId) =>
  api.queryOptions("get", "/experiments/{experiment_id}/agent-session/messages", {
    params: { path: { experiment_id } },
    queryKey: ["agent-session", experiment_id, "messages"]
  });

export const useAgentSessionMessages = (experiment_id: ExperimentId) =>
  useQuery({
    ...agentSessionMessagesOptions(experiment_id),
    enabled: Boolean(experiment_id),
    // Default `throwOnError: true` from queryClient would route 403 (feature flag) and 5xx
    // into the error boundary; we render an inline error state in the chat area instead.
    throwOnError: false
  });

export const agentSessionDocumentsOptions = (experiment_id: ExperimentId) =>
  api.queryOptions("get", "/experiments/{experiment_id}/agent-session/documents", {
    params: { path: { experiment_id } },
    queryKey: ["agent-session", experiment_id, "documents"]
  });

export const useClearAgentSession = () =>
  api.useMutation("delete", "/experiments/{experiment_id}/agent-session", {
    onSuccess: async (_data, vars) => {
      await queryClient.invalidateQueries({
        queryKey: agentSessionMessagesOptions(vars.params.path.experiment_id).queryKey
      });
      // Clearing the session also deletes its documents, so the attachment slots are free again.
      await queryClient.invalidateQueries({
        queryKey: agentSessionDocumentsOptions(vars.params.path.experiment_id).queryKey
      });
    },
    onError: error => {
      notifications.error("Failed to clear conversation", error.message);
    }
  });

export const useSubmitSessionFeedback = () => api.useMutation("post", "/agent-proxy/feedback/");

// The multipart upload type is `FormData` (the openapi codegen collapses the single-`file`
// binary body to a raw FormData), so callers build and pass the form themselves.
export const useUploadSessionDocument = () =>
  api.useMutation("post", "/experiments/{experiment_id}/agent-session/documents", {
    // Every upload path (compose chips and the landing-page bootstrap) must refresh the session
    // document list — the attachment slot count is derived from it.
    onSuccess: async (_data, vars) => {
      await queryClient.invalidateQueries({
        queryKey: agentSessionDocumentsOptions(vars.params.path.experiment_id).queryKey
      });
    }
  });
